import SvgColor from '../../../components/svg-color';
import PublicIcon from "@mui/icons-material/Public";
import { getUserPermissions, getCurrentUser } from 'src/utils/permissionsUtils';

const icon = (name, size = 'medium') => {
  const sizeConfig = {
    small: { width: 0.6, height: 0.6 },
    medium: { width: 1, height: 1 },
  };

  return <SvgColor src={`/assets/icons/navbar/${name}.svg`} sx={sizeConfig[size]} />;
};

const { isClient, isSuperAdmin } = getUserPermissions();
const currentUser = getCurrentUser();
const userMenuItems = currentUser?.admin?.menuItems || [];

const filterForClient = items =>
  isClient ? items.filter(item => userMenuItems.includes(item.key)) : items;

// EngageCraft Fantasy Football admin sidebar.
// Every item and sub-item maps to a real, implemented admin page.

export const navSections = [
  {
    title: 'Users',
    items: filterForClient([
      {
        key: 'users',
        title: 'Users',
        path: '/dashboard/users',
        icon: icon('users'),
        children: [
          {
            key: 'users-list',
            title: 'User List & Search',
            path: '/dashboard/users/list',
          },
          {
            key: 'users-detail-team',
            title: 'User Detail - Team View',
            path: '/dashboard/users/detail-team',
          },
          {
            key: 'users-detail-booster',
            title: 'User Detail - Booster Usage',
            path: '/dashboard/users/detail-booster',
          },
          {
            key: 'users-detail-transfer',
            title: 'User Detail - Transfer History',
            path: '/dashboard/users/detail-transfer',
          },
          {
            key: 'users-segmentation',
            title: 'User Segmentation / Export',
            path: '/dashboard/users/segmentation',
          },
          {
            key: 'users-suspension',
            title: 'Account Suspension / Banning',
            path: '/dashboard/users/suspension',
          },
          {
            key: 'users-gdpr',
            title: 'GDPR / Data Deletion Tools',
            path: '/dashboard/users/gdpr',
          },
          {
            key: 'users-communication',
            title: 'User Communication Tools',
            path: '/dashboard/users/communication',
          },
        ],
      },
      {
        key: 'user-activity',
        title: 'User Activity',
        path: '/dashboard/user-activity',
        icon: icon('transactions'),
        children: [
          {
            key: 'ua-total',
            title: 'Total / Active User Stats',
            path: '/dashboard/user-activity/total',
          },
          {
            key: 'ua-daily-reg',
            title: 'Daily Registration Tracking',
            path: '/dashboard/user-activity/daily-registration',
          },
          {
            key: 'ua-engagement',
            title: 'Engagement / Retention Report',
            path: '/dashboard/user-activity/engagement',
          },
          {
            key: 'ua-audit',
            title: 'Admin Activity Audit Log',
            path: '/dashboard/user-activity/audit',
          },
        ],
      },
      {
        key: 'admins',
        title: 'Admins',
        path: '/dashboard/admins',
        icon: icon('lock-outline'),
      },
    ]),
  },
  {
    title: 'Competition',
    items: filterForClient([
      {
        key: 'tournaments',
        title: 'Tournaments',
        path: '/dashboard/tournaments',
        icon: icon('trophy'),
      },
      {
        key: 'fixtures',
        title: 'Fixtures',
        path: '/dashboard/ecff/fixtures',
        icon: icon('sports'),
      },
    ]),
  },
  {
    title: 'Game Data',
    items: filterForClient([
      {
        key: 'players',
        title: 'Players',
        path: '/dashboard/players',
        icon: icon('people-outline'),
      },
      {
        key: 'squads',
        title: 'Squads',
        path: '/dashboard/ecff/squads',
        icon: icon('union-teams'),
      },
      {
        key: 'opta-squads',
        title: 'OPTA Squads',
        path: '/dashboard/ecff/opta-squads',
        icon: icon('union-teams'),
      },
      {
        key: "areas-countries",
        title: "Areas & Countries",
        path: "/dashboard/areas-countries",
        icon: <PublicIcon sx={{ width: 1, height: 1 }} />,
      },
      {
        key: 'scoring',
        title: 'Scoring Rules',
        path: '/dashboard/scoring-rules',
        icon: icon('scoring'),
      },
      ...(isSuperAdmin
        ? [
            {
              key: 'match-simulator',
              title: 'Match Simulator',
              path: '/dashboard/tools/match-simulator',
              icon: icon('calculator'),
            },
          ]
        : []),
      {
        key: 're-scoring',
        title: 'Re-Scoring',
        path: '/dashboard/tools/re-scoring',
        icon: icon('rewind-right-outline'),
      },
    ]),
  },
  {
    title: 'Engagement',
    items: filterForClient([
      {
        key: 'leagues',
        title: 'Leagues',
        path: '/dashboard/ecff/leagues',
        icon: icon('leagues'),
      },
      {
        key: 'winners',
        title: 'Winners',
        path: '/dashboard/ecff/winners',
        icon: icon('trophy'),
      },
      {
        key: 'weekly-prizes',
        title: 'Weekly Prizes',
        path: '/dashboard/ecff/weekly-prizes',
        icon: icon('trophy'),
      },
      {
        key: 'ecff-opta-import',
        title: 'OPTA Import',
        path: '/dashboard/ecff/opta-import',
        icon: icon('flash-outline'),
        children: [
          {
            key: 'oi-tournaments',
            title: 'Tournament Import',
            path: '/dashboard/ecff/opta-import/tournaments',
          },
          {
            key: 'oi-additional-leagues',
            title: 'Additional Leagues Import',
            path: '/dashboard/ecff/other-league-import',
          },
          {
            key: 'oi-sync-players',
            title: 'Sync Players',
            path: '/dashboard/ecff/opta-import/sync-players',
          },
        ],
      },
      {
        key: 'boosters',
        title: 'Boosters',
        path: '/dashboard/boosters',
        icon: icon('coins-icon'),
        children: [
          {
            key: 'b-configuration',
            title: 'Configuration',
            path: '/dashboard/boosters/configuration',
          },
          {
            key: 'b-usage',
            title: 'Usage Tracking',
            path: '/dashboard/boosters/usage',
          },
        ],
      },
    ]),
  },
  {
    // Read-only proof of what was sent to Gaming Hub: one page per outbox.
    title: 'Delivery Records',
    items: filterForClient([
      {
        key: 'delivery-notifications',
        title: 'Notifications',
        path: '/dashboard/delivery-records/notifications',
        icon: icon('email-outline'),
      },
      {
        key: 'delivery-gamification-awards',
        title: 'Gamification Awards',
        path: '/dashboard/delivery-records/gamification-awards',
        icon: icon('award-outline'),
      },
      {
        key: 'delivery-currencies',
        title: 'Currencies',
        path: '/dashboard/delivery-records/currencies',
        icon: icon('coins'),
      },
    ]),
  },
  {
    title: 'Ops',
    items: filterForClient([
      ...(isSuperAdmin
        ? [
            {
              key: 'app-settings',
              title: 'App Settings',
              path: '/dashboard/app-settings',
              icon: icon('settings-outline'),
            },
          ]
        : []),
      {
        key: 'reports',
        title: 'Reports',
        path: '/dashboard/app',
        icon: icon('ic_analytics'),
      },
    ]),
  },
];
